/*
 * Copyright (c) Dito Technologies
 */

import React from "react";
import {Reducer, State, StepStatus, StepType} from "ui/feature/root/FormReducer";
import RootStepper from "ui/feature/root/RootStepper";
import RootWorkspace from "ui/feature/root/RootWorkspace";
import Header1 from "ui/kit/typography/Header1";
import styles from "./PieceEditBody.module.scss";
import {graphql} from "babel-plugin-relay/macro";
import {createFragmentContainer} from "react-relay";
import {PieceEditBody_piece$data} from "ui/feature/piece-edit/__generated__/PieceEditBody_piece.graphql";

interface Props {
    piece: PieceEditBody_piece$data;
    retry: (() => void) | null;
}

function getUploadStepStatusFromPiece(piece: PieceEditBody_piece$data): StepStatus {
    if (piece.sheets) {
        return StepStatus.SUCCESS
    } else {
        return StepStatus.NEEDS_EDIT
    }
}

function getStaffSelectStepStatus(piece: PieceEditBody_piece$data): StepStatus {
    for (const page of piece.sheets) {
        if (!page.barFinderUserConfirmation || page.barFinderUserConfirmation[0].status !== "SUCCESS") {
            return StepStatus.NEEDS_EDIT;
        }
    }
    return StepStatus.SUCCESS;
}

function getBarFinderUserConfirmationSuccessPageIds(piece: PieceEditBody_piece$data) {
    if (getStaffSelectStepStatus(piece) === StepStatus.SUCCESS) {
        return piece.sheets.map((page) => page.id)
    } else {
        return [];
    }
}

function getNoteFinderUserConfirmationStepStatus(piece: PieceEditBody_piece$data): StepStatus {
    console.log("getting the initial note finder status")
    for (const page of piece.sheets) {
        if (!page.noteFinderUserConfirmation || page.noteFinderUserConfirmation[0].status !== "SUCCESS") {
            console.log("looking for the note finder user confirmation not found");
            return StepStatus.NEEDS_EDIT;
        }
    }
    console.log("success");
    return StepStatus.SUCCESS;
}

function getNoteFinderUserConfirmationSuccessPageIds(piece: PieceEditBody_piece$data) {
    if (getNoteFinderUserConfirmationStepStatus(piece) === StepStatus.SUCCESS) {
        return piece.sheets.map((page) => page.id)
    } else {
        return [];
    }
}

function getNoteFinderUserConfirmationDeletedBars(piece: PieceEditBody_piece$data) {
    let output: string[] = [];
    for (const page of piece.sheets) {
        if (page.barFinderUserConfirmation && page.barFinderUserConfirmation[0].status === "SUCCESS") {
            const storageNames = page.barFinderUserConfirmation[0].data?.deleted_mask_storage_names || [];
            output = [
                ...output,
                ...storageNames
            ]
        }
    }
    return output;
}

function generateInitialState(piece: PieceEditBody_piece$data): State {
    return {
        pieceId: piece.id,
        selectedBar: null,
        barFinderUserConfirmationErrorPageIds: [],
        barFinderUserConfirmationLoadingPageIds: [],
        barFinderUserConfirmationSuccessPageIds: getBarFinderUserConfirmationSuccessPageIds(piece),
        deletedBars: getNoteFinderUserConfirmationDeletedBars(piece),
        selectedStep: StepType.UPLOAD,
        uploadStepStatus: getUploadStepStatusFromPiece(piece),
        staffSelectStepStatus: getStaffSelectStepStatus(piece),
        downloadStepStatus: StepStatus.NEEDS_EDIT,
        files: [],
        fileToUploadProgressLookup: {},
        noteFinderUserConfirmationErrorPageIds: [],
        noteFinderUserConfirmationLoadingPageIds: [],
        noteFinderUserConfirmationSuccessPageIds: getNoteFinderUserConfirmationSuccessPageIds(piece),
        noteFinderUserConfirmationStatus: getNoteFinderUserConfirmationStepStatus(piece)
    }
}

const PieceEditBody: React.FC<Props> = ({piece, retry}) => {

    const initialState = React.useMemo(() => {
        return generateInitialState(piece);
    }, [piece]);

    const [state, dispatch] = React.useReducer(Reducer, initialState)

    console.log("piece here", piece.name);

    return (
        <>
            <Header1>Edit <i>{piece.name || "Unnamed Piece"}</i></Header1>
            <div className={styles.workspaceContainer}>
                <RootStepper state={state} dispatch={dispatch}/>
                <RootWorkspace state={state} dispatch={dispatch}/>
            </div>
        </>
    )
}

export default createFragmentContainer(PieceEditBody, {
    piece: graphql`
    fragment PieceEditBody_piece on MusicPiece {
        id
        name
        sheets {
            id
            noteFinderUserConfirmation: steps(step_type: NOTE_USER_CONFIRMATION) {
                id
                status
            }
            barFinderUserConfirmation: steps(step_type: BAR_FINDER_USER_CONFIRMATION) {
                id
                status
                data {
                    ... on MusicParseStepDataBarFinderUserConfirmation {
                        deleted_mask_storage_names
                    }
                }
            }
        }
    }
    `
});
